/**
 * StepSite.js
 *
 * An onboarding step for selecting the user's school or organization (site).
 * It searches for existing sites and allows adding new ones via Google Places.
 */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import FormTemplate from '@/dialogs_common/SetupTemplate';
import Overlay from '@/forms/_common/Overlay';
import { useOnboarding } from '@/pages/setup/account/OnboardingContext';
import usePotentialSites from '@/hooks/usePotentialSites';
import StepSiteBody from './StepSiteBody';

StepSite.propTypes = {
  user: PropTypes.object,
};

const EMPTY_SITE = {
  siteId: '',
  name: '',
  addressLine1: '',
  addressLine1Short: '',
  addressLine2: '',
  addressLine3: '',
  city: '',
  state: '',
  zipCode: '',
  isActive: false,
  type: 'Custom',
};

/**
 * The site selection step in the onboarding wizard.
 *
 * @param {object} props - Component props.
 * @returns {JSX.Element} The rendered site selection step.
 */
export default function StepSite(props) {
  const { user: userProp } = props;
  const { registerOnBeforeNext, setNextDisabled, data, roleCreate, refetchUser, saving: onboardingSaving } = useOnboarding();
  const { create: potentialSiteCreate, isCreating: isCreatingPotentialSite } = usePotentialSites();
  const saving = onboardingSaving || isCreatingPotentialSite;

  const [siteData, setSiteData] = useState(_.merge({}, EMPTY_SITE, data.siteData));
  const initialSiteData = useRef(siteData);

  // Show a loading overlay until lat/lng are available (they are geocoded
  // server-side after the zip code step). We derive this directly from reactive
  // context data, so no polling is needed. A 5s timeout prevents waiting forever
  // if geocoding fails or is slow.
  const [timedOut, setTimedOut] = useState(false);
  const loading = !timedOut && (!data.latitude || !data.longitude);
  useEffect(() => {
    if (data.latitude && data.longitude) return;
    const timer = setTimeout(() => setTimedOut(true), 5000);
    return () => clearTimeout(timer);
  }, [data.latitude, data.longitude]);

  const userId = data.id || userProp?.id;

  useEffect(() => {
    if (data.roles?.length > 0) {
      const firstRole = data.roles[0];
      const site = firstRole.data.site;
      if (site) {
        const existingSiteData = {
          siteId: site.id,
          name: site.name || '',
          addressLine1: site.addressLine1 || '',
          addressLine1Short: site.addressLine1 || '',
          addressLine2: site.addressLine2 || '',
          addressLine3: site.addressLine3 || '',
          city: site.city || '',
          state: site.state || '',
          zipCode: site.zipCode || '',
          isActive: site.isActive ?? false,
          type: 'Site',
        };
        setSiteData(existingSiteData);
        initialSiteData.current = existingSiteData;
      }
    }
  }, [data.roles]);

  const isConfirmed = siteData.type === 'Site' || siteData.type === 'PotentialSite';

  useEffect(() => {
    setNextDisabled(!isConfirmed);
  }, [isConfirmed, setNextDisabled]);

  /**
   * Updates multiple site data fields at once.
   *
   * @param {object} values - The new site data values.
   */
  function onChangeMultiple(values) {
    setSiteData(_.merge({}, EMPTY_SITE, values));
  }

  /**
   * Handles changes to the site name input.
   *
   * @param {string} value - The new input value.
   */
  function onInputChange(value) {
    if (isConfirmed) {
      setSiteData(_.merge({}, EMPTY_SITE, { name: value }));
    }
  }

  /**
   * Updates site data when a site is selected from the picker.
   *
   * @param {object} site - The selected site object.
   */
  function onSelectSiteFromPicker(site) {
    setSiteData(_.merge({}, EMPTY_SITE, {
      siteId: site.id,
      name: site.data.name || '',
      addressLine1: site.data.addressLine1 || '',
      addressLine1Short: site.data.addressLine1 || '',
      addressLine2: site.data.addressLine2 || '',
      addressLine3: site.data.addressLine3 || '',
      city: site.data.city || '',
      state: site.data.state || '',
      zipCode: site.data.zipCode || '',
      isActive: site.data.isActive ?? false,
      type: 'Site',
    }));
  }

  /**
   * Handles the "none of these" selection from the site picker.
   */
  function onNoneFromPicker() {
    setSiteData((prev) => ({ ...prev, siteId: '', type: 'PotentialSite' }));
  }

  useEffect(() => {
    registerOnBeforeNext(async () => {
      if (siteData.type === 'Site') {
        if (_.isEqual(siteData, initialSiteData.current)) {
          return true;
        }

        // Check if a role already exists for this site
        const existingRole = _.find(data.roles, (r) => r.data.siteId === siteData.siteId);

        if (!existingRole) {
          try {
            await roleCreate({
              userId: userId,
              siteId: siteData.siteId,
              title: 'Classroom Teacher', // Required field, using a default
            });
            await refetchUser();
          } catch (e) {
            console.error('Failed to create site role', e);
            return false;
          }
        }
      } else if (siteData.type === 'PotentialSite') {
        if (siteData.siteId) {
          // Already created or existing potential site
          return true;
        }

        try {
          await potentialSiteCreate({
            name: siteData.name,
            addressLine1: siteData.addressLine1,
            addressLine2: siteData.addressLine2,
            city: siteData.city,
            state: siteData.state,
            zipCode: siteData.zipCode,
          });
          await refetchUser();
        } catch (e) {
          console.error('Failed to create potential site', e);
          return false;
        }
      }

      return true;
    });
  }, [siteData, registerOnBeforeNext, roleCreate, potentialSiteCreate, userId, refetchUser, data.roles]);

  return (
    <div className="mt-6">
      <Overlay isVisible={loading || saving}>
        <FormTemplate
          title="Where do you work with learners?"
          subtitle="Sharing your work location helps us connect you with relevant partners and opportunities. We'll also check to see if your school or organization is already participating."
          body={(
            <StepSiteBody
              siteData={siteData}
              zipCode={data.zipCode}
              latitude={data.latitude}
              longitude={data.longitude}
              onChangeMultiple={onChangeMultiple}
              onInputChange={onInputChange}
              onSelectSite={onSelectSiteFromPicker}
              onNone={onNoneFromPicker}
            />
          )}
        />
      </Overlay>
    </div>
  );
}
